import { useState } from "react";
import { Form, Row, FormGroup, Label, Input, Button, Col } from "reactstrap";
import {
    evaluacionRiesgoInherente,
    evaluacionMitigantes,
    crearEstadoRiesgoInherente,
    crearEstadoMitigantes,
    buildPayloadCuestionario,
} from "./utils";
import { TIPO_ACTIVIDAD_VULNERABLE } from "../Utils.global";
import CalculadorRiesgo from "./CalculadorRiesgo";
import Requester from "../shared/Requester";

const initialDatosUnidad = {
    nombre: '',
    tipoActividad: '',
    actividad12Meses: '',
    efectivoClientes: '',
    negociosExtranjeros: ''
};

function todasRespondidas(estado, ids) {
    return ids.every((id) => estado[id] !== '' && estado[id] !== undefined);
}

function PreguntaRiesgo({ pregunta, valor, onChange }) {
    return (
        <FormGroup tag="fieldset" className="mb-4">
            <legend className="fs-6 fw-normal">{pregunta.texto}</legend>
            {pregunta.opciones.map((opcion) => (
                <FormGroup check key={opcion.value} className="mb-2">
                    <Label check>
                        <Input
                            type="radio"
                            name={pregunta.id}
                            value={opcion.value}
                            checked={valor !== '' && Number(valor) === opcion.value}
                            onChange={() => onChange(pregunta.id, opcion.value)}
                        />
                        {' '}{opcion.etiqueta}
                    </Label>
                </FormGroup>
            ))}
        </FormGroup>
    );
}

function PreguntaMitigante({ item, valor, onChange }) {
    return (
        <FormGroup tag="fieldset" className="mb-4">
            <legend className="fs-6">{item.titulo}</legend>
            <p className="mb-2">{item.texto}</p>
            <small className="text-muted d-block mb-2">{item.ayuda}</small>
            {item.opciones.map((opcion) => (
                <FormGroup check key={opcion.value} className="mb-2">
                    <Label check>
                        <Input
                            type="radio"
                            name={item.id}
                            value={opcion.value}
                            checked={valor !== '' && Number(valor) === opcion.value}
                            onChange={() => onChange(item.id, opcion.value)}
                        />
                        {' '}{opcion.etiqueta}
                    </Label>
                </FormGroup>
            ))}
        </FormGroup>
    );
}

export default function UnidadesDeNegocio() {
    const [seccion, setSeccion] = useState(1);
    const [datosUnidad, setDatosUnidad] = useState(initialDatosUnidad);
    const [riesgoInherente, setRiesgoInherente] = useState(crearEstadoRiesgoInherente);
    const [mitigantes, setMitigantes] = useState(crearEstadoMitigantes);
    const [errorValidacion, setErrorValidacion] = useState('');

    const sinActividad12Meses = datosUnidad.actividad12Meses === 'no';
    const idsRiesgo = Object.keys(riesgoInherente);
    const idsMitigantes = Object.keys(mitigantes);

    function handleDatosUnidadChange(name) {
        return function (e) {
            setDatosUnidad({ ...datosUnidad, [name]: e.target.value });
            setErrorValidacion('');
        };
    }

    function handleRiesgoChange(id, value) {
        setRiesgoInherente({ ...riesgoInherente, [id]: value });
        setErrorValidacion('');
    }

    function handleMitiganteChange(id, value) {
        setMitigantes({ ...mitigantes, [id]: value });
        setErrorValidacion('');
    }

    function validarSeccion1() {
        if (!datosUnidad.nombre.trim()) {
            return 'Indica el nombre de la unidad de negocio.';
        }
        if (!datosUnidad.tipoActividad) {
            return 'Selecciona el tipo de actividad vulnerable.';
        }
        if (!datosUnidad.actividad12Meses) {
            return 'Indica si cuentas con al menos 12 meses de actividad.';
        }
        if (sinActividad12Meses) {
            if (!datosUnidad.efectivoClientes || !datosUnidad.negociosExtranjeros) {
                return 'Completa las proyecciones para los próximos 12 meses.';
            }
        }
        return '';
    }

    function handleContinuarSeccion1(e) {
        e.preventDefault();
        const error = validarSeccion1();
        if (error) {
            setErrorValidacion(error);
            return;
        }
        setSeccion(2);
    }

    function handleContinuarSeccion2(e) {
        e.preventDefault();
        if (!todasRespondidas(riesgoInherente, idsRiesgo)) {
            setErrorValidacion('Responde todas las preguntas de la evaluación de riesgo inherente.');
            return;
        }
        setErrorValidacion('');
        setSeccion(3);
    }

    async function handleFinalizar(e) {
        e.preventDefault();
        if (!todasRespondidas(mitigantes, idsMitigantes)) {
            setErrorValidacion('Responde todas las preguntas sobre mitigantes y controles internos.');
            return;
        }
        setErrorValidacion('');

        const payload = buildPayloadCuestionario({
            actividadVulnerable: datosUnidad.tipoActividad,
            unidadId: datosUnidad.unidadId || null,
            riesgoInherenteState: riesgoInherente,
            mitigantesState: mitigantes,
        });

        try {
            await Requester.post('cuestionario', payload);
        } catch (err) {
            console.log('Error al guardar cuestionario:', err);
            setErrorValidacion('Ocurrió un error al guardar la evaluación. Intenta de nuevo.');
        }
    }

    return (
        <div>
            <h3>Unidades de negocio</h3>
            <Row>
                <p>Ayudanos a conocer tu organización, proporcionanos la información de tus unidades de negocio.</p>
            </Row>

            <CalculadorRiesgo riesgoInherente={riesgoInherente} mitigantes={mitigantes} />

            {errorValidacion && (
                <p className="text-danger">{errorValidacion}</p>
            )}

            <Form onSubmit={seccion === 1 ? handleContinuarSeccion1 : seccion === 2 ? handleContinuarSeccion2 : handleFinalizar}>
                {seccion === 1 && (
                    <>
                        <h5 className="mt-3">Datos generales de la unidad</h5>
                        <FormGroup>
                            <Label for="nombreUnidad">Nombre de la unidad de negocio *</Label>
                            <Input
                                type="text"
                                id="nombreUnidad"
                                className="FormInput"
                                value={datosUnidad.nombre}
                                onChange={handleDatosUnidadChange('nombre')}
                                required
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label for="tipoActividad">Tipo de actividad vulnerable *</Label>
                            <Input
                                type="select"
                                id="tipoActividad"
                                className="FormInput"
                                value={datosUnidad.tipoActividad}
                                onChange={handleDatosUnidadChange('tipoActividad')}
                                required
                            >
                                <option value="">Seleccionar...</option>
                                {TIPO_ACTIVIDAD_VULNERABLE.map((item) => (
                                    <option key={item.id} value={item.id}>{item.name}</option>
                                ))}
                            </Input>
                        </FormGroup>
                        <FormGroup>
                            <Label for="actividad12Meses">¿Cuentas con actividad previa de al menos 12 meses? *</Label>
                            <Input
                                type="select"
                                id="actividad12Meses"
                                className="FormInput"
                                value={datosUnidad.actividad12Meses}
                                onChange={handleDatosUnidadChange('actividad12Meses')}
                                required
                            >
                                <option value="">Seleccionar...</option>
                                <option value="si">Sí</option>
                                <option value="no">No</option>
                            </Input>
                        </FormGroup>

                        {sinActividad12Meses && (
                            <>
                                <p>Las siguientes preguntas serán basadas en las proyecciones de tu unidad de negocio que planeas realizar en los próximos 12 meses.</p>
                                <p>Te recordaremos cambiar a transacciones reales dentro de 12 meses.</p>
                                <FormGroup>
                                    <Label for="efectivoClientes">¿Planeas recibir dinero en efectivo de tus clientes? *</Label>
                                    <Input
                                        type="select"
                                        id="efectivoClientes"
                                        className="FormInput"
                                        value={datosUnidad.efectivoClientes}
                                        onChange={handleDatosUnidadChange('efectivoClientes')}
                                        required
                                    >
                                        <option value="">Seleccionar...</option>
                                        <option value="si">Sí</option>
                                        <option value="no">No</option>
                                    </Input>
                                </FormGroup>
                                <FormGroup>
                                    <Label for="negociosExtranjeros">¿Planeas llevar a cabo negocios con empresas extranjeras? *</Label>
                                    <Input
                                        type="select"
                                        id="negociosExtranjeros"
                                        className="FormInput"
                                        value={datosUnidad.negociosExtranjeros}
                                        onChange={handleDatosUnidadChange('negociosExtranjeros')}
                                        required
                                    >
                                        <option value="">Seleccionar...</option>
                                        <option value="si">Sí</option>
                                        <option value="no">No</option>
                                    </Input>
                                </FormGroup>
                            </>
                        )}

                        <Row className="mt-3">
                            <Col className="text-end">
                                <Button color="secondary" type="submit">Continuar</Button>
                            </Col>
                        </Row>
                    </>
                )}

                {seccion === 2 && (
                    <>
                        <h5 className="mt-3">Sección 2: Evaluación del riesgo inherente institucional</h5>
                        <p className="text-muted">
                            Mide la exposición en bruto de la empresa sobre los 4 pilares obligatorios de la ley.
                        </p>

                        {evaluacionRiesgoInherente.map((pilar) => (
                            <div key={pilar.pilar} className="mb-4">
                                <h6>{pilar.titulo}</h6>
                                {pilar.preguntas.map((pregunta) => (
                                    <PreguntaRiesgo
                                        key={pregunta.id}
                                        pregunta={pregunta}
                                        valor={riesgoInherente[pregunta.id]}
                                        onChange={handleRiesgoChange}
                                    />
                                ))}
                            </div>
                        ))}

                        <Row className="mt-3">
                            <Col>
                                <Button color="link" type="button" className="p-0" onClick={() => setSeccion(1)}>
                                    Volver
                                </Button>
                            </Col>
                            <Col className="text-end">
                                <Button color="secondary" type="submit">Continuar</Button>
                            </Col>
                        </Row>
                    </>
                )}

                {seccion === 3 && (
                    <>
                        <h5 className="mt-3">Sección 3: Evaluación de eficacia de mitigantes y controles internos</h5>
                        <p className="text-muted">
                            Mide la efectividad porcentual de los controles implementados por la empresa (0% a 100%).
                        </p>

                        {evaluacionMitigantes.map((item) => (
                            <PreguntaMitigante
                                key={item.id}
                                item={item}
                                valor={mitigantes[item.id]}
                                onChange={handleMitiganteChange}
                            />
                        ))}

                        <Row className="mt-3">
                            <Col>
                                <Button color="link" type="button" className="p-0" onClick={() => setSeccion(2)}>
                                    Volver
                                </Button>
                            </Col>
                            <Col className="text-end">
                                <Button color="secondary" type="submit">Guardar evaluación</Button>
                            </Col>
                        </Row>
                    </>
                )}
            </Form>
        </div>
    );
}
